import React, { useContext, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Modal,
  TextField,
  useTheme
} from '@mui/material';
import Button from '@mui/material/Button';
import { Close as CloseIcon } from '@mui/icons-material';
import { I_Folder } from 'src/interfaces/InstallationTypes';
import { TokenContext } from 'src/contexts/tokenContext';
import { InstallationsContext } from 'src/contexts/InstallationsContextProvider';
import { FormattedMessage } from 'react-intl';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';

interface folderFormProps {
  cancel: () => void;
  submit: () => void;
  parentid: number;
}

function folderForm(props: folderFormProps) {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [formValues, setFormValues] = useState<Partial<I_Folder>>({
    name: '',
    information: ''
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const requiredFields = ['name'];
  const { product, setProduct } = useContext(ProductIdContext);
  const tokencontext = useContext(TokenContext);
  const { removeToken } = tokencontext;

  const installationContext = useContext(InstallationsContext);
  const { loading, setLoading, error, setError, createFolder } =
    installationContext;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };
  const handleSubmit = async (e) => {
    formValues.parentId = props.parentid;
    setLoading(true);
    const failure = await createFolder(formValues, product);

    // Keep the dialog open on a refused name so the operator can correct it. CreateFolder now
    // refuses a second folder of the same name under the same parent — closing regardless would
    // report success for a folder that was never created.
    if (failure !== null) {
      setSubmitError(
        failure === 409 ? 'folderNameTaken' : 'folderCreateFailed'
      );
      return;
    }

    setSubmitError(null);
    props.submit();
  };
  const handleCancelSubmit = (e) => {
    props.cancel();
  };

  const isMobile = window.innerWidth <= 1490;

  const areRequiredFieldsFilled = () => {
    for (const field of requiredFields) {
      if (!formValues[field]) {
        return false;
      }
    }
    return true;
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => {}}
        aria-labelledby="error-modal"
        aria-describedby="error-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: isMobile ? '50%' : '40%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90vw', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 4,
            boxShadow: 24,
            p: 4
          }}
        >
          <Box
            component="form"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center', // Center items horizontally
              '& .MuiTextField-root': {
                m: 1,
                width: { xs: '100%', md: 390 }
              }
            }}
            noValidate
            autoComplete="off"
          >
            <div>
              <TextField
                label={<FormattedMessage id="name" defaultMessage="Name" />}
                name="name"
                value={formValues.name}
                onChange={handleChange}
                required
                error={formValues.name === ''}
              />
            </div>

            <div>
              <TextField
                label={
                  <FormattedMessage
                    id="information"
                    defaultMessage="Information"
                  />
                }
                name="information"
                value={formValues.information}
                onChange={handleChange}
              />
            </div>
          </Box>

          {/* Shown in place of closing the dialog: a refused name has to be correctable here. */}
          {submitError !== null && (
            <Alert severity="error" sx={{ mt: 1 }}>
              <FormattedMessage
                id={submitError}
                defaultMessage="Could not create the folder."
              />
            </Alert>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginTop: 10
            }}
          >
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                marginLeft: '20px'
              }}
              disabled={!areRequiredFieldsFilled()}
            >
              <FormattedMessage id="submit" defaultMessage="Submit" />
            </Button>

            <Button
              variant="contained"
              onClick={handleCancelSubmit}
              sx={{
                marginLeft: '10px'
              }}
            >
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>

            {loading && (
              <CircularProgress
                sx={{
                  color: theme.palette.primary.main,
                  marginLeft: '20px'
                }}
              />
            )}

            {error && (
              <Alert
                severity="error"
                sx={{
                  ml: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <FormattedMessage
                  id="errorOccured"
                  defaultMessage="An error has occurred"
                />
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => setError(false)}
                  sx={{ marginLeft: '4px' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Alert>
            )}
          </div>
        </Box>
      </Modal>
    </>
  );
}

export default folderForm;
